import { selectLatestVendorInvite } from './selectLatestVendorInvite'
import { type VendorInvite } from '../../data-access/types/VendorInvite'
import {
  type VendorInviteStatusEnum,
  VendorInviteStatuses,
} from '../../entities/enums/VendorInviteStatusEnum'

// ACCEPTED is intentionally excluded pending confirmation with product/backend
// on whether it should be cancellable (VND-383).
const PENDING_STATUSES: VendorInviteStatusEnum[] = [
  VendorInviteStatuses.SENT,
  VendorInviteStatuses.VIEWED,
]

export interface VendorPortalConnection {
  isConnectedToPortal: boolean
  /**
   * A connection existed once but no longer does. History may exist, so
   * callers keep the thread reachable read-only instead of hiding it behind
   * the "invite them" hint (VND-108 AC14).
   */
  wasConnectedToPortal: boolean
  /**
   * The newest invite row, which is what the pane's status pill and the
   * onboarding summary read: unlike the flags above, they report where this
   * vendor stands now rather than whether a connection ever existed.
   */
  latestInvite: Nullable<VendorInvite>
  pendingInvite: Nullable<VendorInvite>
}

/** Where a vendor stands with the portal, derived from its invite rows. */
export const getVendorPortalConnection = (
  invites: VendorInvite[]
): VendorPortalConnection => {
  // Deliberately a reduction over every row rather than the latest invite's
  // status: VendorOverview gates portal-sourced tax details on this flag, and
  // that data stays valid while a newer invite row is mid-flight.
  const isConnectedToPortal = invites.some(
    (invite) => invite.status === VendorInviteStatuses.CONNECTED
  )

  return {
    isConnectedToPortal,
    wasConnectedToPortal:
      !isConnectedToPortal &&
      invites.some(
        (invite) => invite.status === VendorInviteStatuses.DISCONNECTED
      ),
    latestInvite: selectLatestVendorInvite(invites),
    pendingInvite: selectLatestVendorInvite(
      invites.filter((invite) => PENDING_STATUSES.includes(invite.status))
    ),
  }
}
